import * as THREE from 'three';
import type { Point, UnitState } from '../../../shared/game/world-types.js';
import { PING_SECONDS } from '../../../shared/network/pings.js';

/**
 * A glance: the head turns to a point while the body keeps its heading. Presentation only: the server's facing and every outcome are
 * untouched. `GlancePicker` chooses what each unit looks at from state every client has, so every screen shows the same glance;
 * `HeadGlance` eases one figure's head onto it.
 */

/**
 * How far a head turns from the body's heading (rad): a point out to `GLANCE_REACH` is looked toward as far as the turn goes, one further
 * behind is let go. And how far it tips up or down.
 */
export const GLANCE_YAW = THREE.MathUtils.degToRad(90);
export const GLANCE_REACH = THREE.MathUtils.degToRad(130);
export const GLANCE_PITCH = THREE.MathUtils.degToRad(25);
/**
 * How quickly a glance takes the head and lets it go (about a quarter second); how quickly a held glance swings over to a new point, and
 * the fastest it swings (rad/s), so a change of target is an unhurried look across rather than a snap.
 */
const GLANCE_EASE = 12, GLANCE_FOLLOW = 4, GLANCE_SWING = 2.5;
/** Nothing nearer than this is glanced at: the angles turn wild over a point at the feet. */
const GLANCE_NEAREST = .3;

/** A world point to look at: ground x and z, and the height of what is there (a face, the ground under a ping). */
export interface GlancePoint extends Point { y: number }

/** One figure's glance, eased: the direction it holds in the world (so a body turning under it does not drag it along) and its share. */
export class HeadGlance {
  private worldYaw = 0;
  private pitch = 0;
  private weight = 0;
  /** The glance's turn this frame, from the body's heading: yaw (rad, as `rotation.y`) and pitch (rad, positive tips the face down). */
  readonly turn = { yaw: 0, pitch: 0 };
  update(eye: THREE.Vector3, facing: number, at: GlancePoint | undefined, allowed: boolean, dt: number) {
    let want = 0;
    if (at) {
      const dx = at.x - eye.x, dz = at.z - eye.z, flat = Math.hypot(dx, dz);
      if (flat > GLANCE_NEAREST) {
        const yaw = Math.atan2(dx, dz), pitch = THREE.MathUtils.clamp(Math.atan2(eye.y - at.y, flat), -GLANCE_PITCH, GLANCE_PITCH);
        // A glance that starts from the body's heading turns out from it; one already held swings across to the next point.
        if (this.weight < .01) { this.worldYaw = yaw; this.pitch = pitch; }
        else {
          const step = wrap(yaw - this.worldYaw) * (1 - Math.exp(-GLANCE_FOLLOW * dt));
          this.worldYaw += THREE.MathUtils.clamp(step, -GLANCE_SWING * dt, GLANCE_SWING * dt);
          this.pitch = THREE.MathUtils.damp(this.pitch, pitch, GLANCE_FOLLOW, dt);
        }
        // A point just past the turn is looked toward from the limit; only one well behind is let go.
        want = allowed && Math.abs(wrap(yaw - facing)) <= GLANCE_REACH ? 1 : 0;
      }
    }
    this.weight = allowed ? THREE.MathUtils.damp(this.weight, want, GLANCE_EASE, dt) : THREE.MathUtils.damp(this.weight, 0, GLANCE_EASE * 1.5, dt);
    if (this.weight < 1e-3 && want === 0) this.weight = 0;
    this.turn.yaw = this.weight * THREE.MathUtils.clamp(wrap(this.worldYaw - facing), -GLANCE_YAW, GLANCE_YAW);
    this.turn.pitch = this.weight * this.pitch;
    return this.turn;
  }
  reset() { this.weight = 0; this.turn.yaw = 0; this.turn.pitch = 0; }
}

const wrap = (angle: number) => Math.atan2(Math.sin(angle), Math.cos(angle));
const UP = new THREE.Vector3(0, 1, 0);
const parentTurn = new THREE.Quaternion(), rootQuaternion = new THREE.Quaternion(), parentQuaternion = new THREE.Quaternion(), axis = new THREE.Vector3();

/**
 * Lays a glance over a finished pose: each joint (chest, neck, head) takes its `yaw` and `pitch` shares of the turn about the root's up axis
 * and the turned face's side axis, in its parent's frame, so the turn reads the same whatever the clip has done to the joints above.
 */
export function turnHead(root: THREE.Object3D, joints: readonly { joint?: THREE.Object3D; yaw: number; pitch: number }[], turn: { yaw: number; pitch: number }) {
  if (!turn.yaw && !turn.pitch) return;
  root.getWorldQuaternion(rootQuaternion);
  const side = new THREE.Vector3(Math.cos(turn.yaw), 0, -Math.sin(turn.yaw));
  for (const { joint, yaw, pitch } of joints) {
    if (!joint?.parent) continue;
    const toParent = joint.parent.getWorldQuaternion(parentQuaternion).invert().multiply(rootQuaternion);
    if (yaw) joint.quaternion.premultiply(parentTurn.setFromAxisAngle(axis.copy(UP).applyQuaternion(toParent), turn.yaw * yaw));
    if (pitch) joint.quaternion.premultiply(parentTurn.setFromAxisAngle(axis.copy(side).applyQuaternion(toParent), turn.pitch * pitch));
  }
}

/** A unit as the picker sees it: where it stands, how tall it is, and its state. */
export interface GlanceUnit { state: UnitState; x: number; y: number; z: number; height: number }
/** A ping on the ground, `age` seconds old. */
export interface GlancePing { at: GlancePoint; age: number }

/** How near a ping, a threat closing in, and an idle hero's passer-by must be (m). */
const PING_REACH = 16, THREAT_REACH = 6, IDLE_REACH = 8;
/** A foe nearer than this (m) is watched when nothing else claims the eyes. */
const FOE_REACH = 12;
/** Closing faster than this (m/s) is coming at you. */
const THREAT_CLOSING = .6;
/** A ping draws eyes for this long of its life (s). */
const PING_LOOK = Math.min(2.5, PING_SECONDS);
/** Idle heroes: every `IDLE_EVERY` seconds of game time, about `IDLE_CHANCE` of them glance at someone for `IDLE_HOLD` seconds. */
const IDLE_EVERY = 9, IDLE_CHANCE = .55, IDLE_HOLD = [1.6, 3] as const;
/** A face is looked at a little under the top of the head. */
const FACE = .92;

const isHero = (state: UnitState) => 'playerSlot' in state;
const hash = (text: string, n: number) => {
  let h = 2166136261 ^ n;
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return ((h >>> 0) % 10007) / 10007;
};

/**
 * Chooses what each unit glances at, in order: a ping (heroes, who share the room's pings), a foe closing in, its current target, a noise it
 * heard (a foe's `lookAt`), the nearest foe, and, for a hero standing about out of a fight, now and then whoever is near. Every choice reads only the
 * snapshot, the pings and game time, so every screen picks the same.
 */
export class GlancePicker {
  /** Last frame's distance between each looker and each foe near it, to tell who is closing in. */
  private gaps = new Map<string, number>();
  private time?: number;
  pick(units: readonly GlanceUnit[], pings: readonly GlancePing[], gameTime: number, inCombat: boolean): Map<string, GlancePoint> {
    const out = new Map<string, GlancePoint>(), dt = this.time === undefined ? 0 : gameTime - this.time, gaps = new Map<string, number>();
    if (dt < 0) this.gaps.clear();
    this.time = gameTime;
    const byId = new Map(units.map(unit => [unit.state.id, unit]));
    const face = (unit: GlanceUnit): GlancePoint => ({ x: unit.x, y: unit.y + unit.height * FACE, z: unit.z });
    for (const unit of units) {
      const state = unit.state;
      if (state.health.dead) continue;
      const hero = isHero(state);
      const seen = (other: GlanceUnit) => other !== unit && !other.state.health.dead && (hero || !other.state.combat.hiding);
      // A ping: the newest one in reach.
      if (hero) {
        let ping: GlancePing | undefined;
        for (const candidate of pings) if (candidate.age < PING_LOOK && Math.hypot(candidate.at.x - unit.x, candidate.at.z - unit.z) < PING_REACH && (!ping || candidate.age < ping.age)) ping = candidate;
        if (ping) { out.set(state.id, ping.at); }
      }
      // A foe closing in: the nearest one coming at it.
      let threat: GlanceUnit | undefined, nearest = Infinity, near: GlanceUnit | undefined, nearGap = FOE_REACH;
      for (const other of units) {
        if (isHero(other.state) === hero || !seen(other)) continue;
        const gap = Math.hypot(other.x - unit.x, other.z - unit.z);
        if (gap < nearGap) { near = other; nearGap = gap; }
        if (gap > THREAT_REACH + 2) continue;
        const key = `${state.id}>${other.state.id}`, before = this.gaps.get(key);
        gaps.set(key, gap);
        if (gap < THREAT_REACH && before !== undefined && dt > 0 && (before - gap) / dt > THREAT_CLOSING && gap < nearest) { threat = other; nearest = gap; }
      }
      if (out.has(state.id)) continue;
      if (threat) { out.set(state.id, face(threat)); continue; }
      // Its target, then a noise it heard.
      const target = state.combat.targetId ? byId.get(state.combat.targetId) : undefined;
      if (target && seen(target)) { out.set(state.id, face(target)); continue; }
      const heard = (state as { lookAt?: Point }).lookAt;
      if (heard) { out.set(state.id, { x: heard.x, y: unit.y + unit.height * FACE, z: heard.z }); continue; }
      // With nothing else on its mind, it keeps an eye on the nearest foe (a foe only once it has the party's measure: its patrol does not
      // stare at heroes it has not noticed).
      if (near && (hero || inCombat || (state as { engaged?: boolean }).engaged)) { out.set(state.id, face(near)); continue; }
      // Standing about: now and then, a look at someone near, a passer-by first.
      if (hero && !inCombat && !state.route.length) {
        const slot = Math.floor((gameTime + hash(state.id, 0) * IDLE_EVERY) / IDLE_EVERY), into = (gameTime + hash(state.id, 0) * IDLE_EVERY) % IDLE_EVERY;
        if (hash(state.id, slot) >= IDLE_CHANCE || into > THREE.MathUtils.lerp(IDLE_HOLD[0], IDLE_HOLD[1], hash(state.id, slot + 1))) continue;
        const near = units.filter(other => seen(other) && Math.hypot(other.x - unit.x, other.z - unit.z) < IDLE_REACH).sort((a, b) => a.state.id < b.state.id ? -1 : 1);
        const passing = near.filter(other => other.state.route.length > 0), pool = passing.length ? passing : near;
        if (pool.length) out.set(state.id, face(pool[Math.floor(hash(state.id, slot + 2) * pool.length)]));
      }
    }
    this.gaps = gaps;
    return out;
  }
  clear() { this.gaps.clear(); this.time = undefined; }
}
